import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { getDeliveries, fulfillDeliveries, createSingleOrder, uploadOrdersExcel, fetchDeliveryFilterOptions, downloadAllLabels, downloadSelectedLabels, fetchCourierOptions, courierOptionsKey } from './deliveriesThunks';
import { setDeliveryItemsCount } from '../deliveryItems/deliveryItemsThunks';

const getDynamicDates = () => {
  const today = new Date();
  const pastDate = new Date(today);
  pastDate.setDate(pastDate.getDate() - 3);

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    start_date: formatDate(pastDate),
    end_date: formatDate(today)
  };
};

const initialState = {
  data: [],
  total: 0,
  statusCount: [],
  globalTotal: 0,
  globalStatusCount: [],
  /**
   * The admin's companies, `{value: id, label: social_reason}`.
   *
   * Not persisted along with the filters: it is a catalog, and a company added
   * or renamed on the backend should show up on the next login rather than be
   * restored from disk.
   */
  companyOptions: [],
  companyOptionsLoaded: false,
  isLoading: false,
  /**
   * Kept apart from `isLoading`, which drives the deliveries table: creating an
   * order happens on a different screen and must not blank the list.
   */
  isSubmittingUpload: false,
  /**
   * The bulk actions run apart from `isLoading`, which is the table's spinner:
   * preparing or printing a selection must not blank the rows it acts on.
   */
  isFulfilling: false,
  isDownloadingLabels: false,
  /**
   * The couriers the bulk «Cambiar courier» offers, by operator (see
   * `courierOptionsKey`), and why they did not load. A catalog: not persisted.
   */
  courierOptions: {},
  courierOptionsError: {},
  error: null,
  /** The latest listing request: one answering after it must not paint over it. */
  listRequestId: null,
  filters: {
    ...getDynamicDates(),
    company: 'all',
    /**
     * Which operator's shipments to show. Only staff can narrow by one — for
     * anybody else the backend forces its own admin and ignores the parameter.
     */
    admin_id: 'all',
    search: '',
    delivery_status: 'all',
    assignedDriverFilter: 'all',
    // 'all' | 'assigned' | 'not_assigned' — the backend ignores any other
    // value, so 'all' is the same as not filtering.
    upsell_filter: 'all',
    // Filtro avanzado (addon): one criterion — 'order_id' | 'reference_id' |
    // 'commune' | 'courier' — and its comma-separated values. Both empty is
    // "no advanced filter"; the pages neutralise a persisted one when the
    // plan lost the addon, the same way they do with the driver filter.
    advanced_filter_field: '',
    advanced_filter_values: '',
    page: 1,
    rowsPerPage: 20,
    orderFilter: '',
    orderByFilter: ''
  }
};

const deliveriesSlice = createSlice({
  name: 'deliveries',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeliveries.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(getDeliveries.fulfilled, (state, action) => {
        if (state.listRequestId && action.meta.requestId !== state.listRequestId) return;
        state.isLoading = false;
        state.data = action.payload.deliveries || [];
        state.total = action.payload.total_deliveries || 0;
        state.statusCount = action.payload.status_count || [];

        // Save global stats if no status filter is applied
        const appliedStatus = action.meta.arg?.delivery_status;
        if (!appliedStatus || appliedStatus === 'all') {
          state.globalTotal = action.payload.total_deliveries || 0;
          state.globalStatusCount = action.payload.status_count || [];
        }
      })
      .addCase(getDeliveries.rejected, (state, action) => {
        if (state.listRequestId && action.meta.requestId !== state.listRequestId) return;
        state.isLoading = false;
        state.error = action.payload;
      })
      // Patched in place rather than refetched: the listing reads a replica
      // that can still be behind the write.
      .addCase(setDeliveryItemsCount.fulfilled, (state, action) => {
        const orderId = String(action.meta.arg.orderId);
        state.data.forEach((row) => {
          if (String(row.order_id) === orderId) row.total_items = action.payload.count;
        });
      })
      .addCase(fetchDeliveryFilterOptions.fulfilled, (state, action) => {
        state.companyOptions = (action.payload.companies || []).map((company) => ({
          value: company.id,
          label: company.social_reason || `Empresa ${company.id}`,
        }));
        state.companyOptionsLoaded = true;
      })
      .addCase(fetchCourierOptions.pending, (state, action) => {
        delete state.courierOptionsError[courierOptionsKey(action.meta.arg)];
      })
      .addCase(fetchCourierOptions.fulfilled, (state, action) => {
        state.courierOptions[courierOptionsKey(action.meta.arg)] = action.payload;
      })
      .addCase(fetchCourierOptions.rejected, (state, action) => {
        if (action.meta.condition) return;
        state.courierOptionsError[courierOptionsKey(action.meta.arg)] =
          action.payload || 'No se pudieron cargar los couriers';
      })
      // The bulk changes report through toasts and their dialogs. Their errors
      // stay out of `error`, which is the table's: a failed "prepare" used to
      // land there and be shown, or not, depending on how many rows the table had.
      .addCase(fulfillDeliveries.pending, (state) => {
        state.isFulfilling = true;
      })
      // Order upload (manual form and spreadsheet). Its success is a toast, see
      // `deliveriesFeedback`.
      .addCase(createSingleOrder.pending, (state) => {
        state.isSubmittingUpload = true;
        state.error = null;
      })
      .addCase(createSingleOrder.fulfilled, (state) => {
        state.isSubmittingUpload = false;
      })
      .addCase(createSingleOrder.rejected, (state, action) => {
        state.isSubmittingUpload = false;
        state.error = action.payload;
      })
      .addCase(uploadOrdersExcel.pending, (state) => {
        state.isSubmittingUpload = true;
        state.error = null;
      })
      .addCase(uploadOrdersExcel.fulfilled, (state) => {
        state.isSubmittingUpload = false;
      })
      .addCase(uploadOrdersExcel.rejected, (state, action) => {
        state.isSubmittingUpload = false;
        state.error = action.payload;
      })
      .addMatcher(isAnyOf(fulfillDeliveries.fulfilled, fulfillDeliveries.rejected), (state) => {
        state.isFulfilling = false;
      })
      .addMatcher(isAnyOf(downloadAllLabels.pending, downloadSelectedLabels.pending), (state) => {
        state.isDownloadingLabels = true;
      })
      .addMatcher(
        isAnyOf(
          downloadAllLabels.fulfilled,
          downloadAllLabels.rejected,
          downloadSelectedLabels.fulfilled,
          downloadSelectedLabels.rejected
        ),
        (state) => {
          state.isDownloadingLabels = false;
        }
      );
  }
});

export const { setFilters } = deliveriesSlice.actions;

const persistConfig = {
  key: 'deliveries',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, deliveriesSlice.reducer);
